import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Grid, Typography, Box, Alert, Skeleton
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { fetchFmDataProducts } from '../../lib/services/fmDataService';

const ApiHubPage = ({ idToken, drawerWidth, isEmbedded }) => {
    const [dataProducts, setDataProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!idToken) return;
        setLoading(true);
        fetchFmDataProducts(idToken, true)
            .then((response) => {
                if (!response.isError) {
                    const rows = (response.data || []).map((p, i) => ({
                        id: i,
                        productId: p.id,
                        name: p.userFriendlyAliasName || p.name,
                        description: p.description || '',
                        inceptionDateTime: p.inceptionDateTime ? new Date(p.inceptionDateTime).toLocaleDateString() : 'N/A',
                        lastRefreshedDateTime: p.lastRefreshedDateTime ? new Date(p.lastRefreshedDateTime).toLocaleDateString() : 'N/A',
                        alias: p.userFriendlyAliasName || ''
                    }));
                    setDataProducts(rows);
                    setError(null);
                } else {
                    setError(response.data || 'Failed to load data products');
                }
            })
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [idToken]);

    const contentOffset = Math.max((drawerWidth || 0) - 15, 0);
    const containerStyle = {
        marginLeft: contentOffset,
        width: `calc(100% - ${contentOffset}px)`,
        boxSizing: 'border-box'
    };

    const columns = [
        {
            field: 'name',
            headerName: 'Data Product Name',
            flex: 2,
            headerClassName: 'super-app-theme--header'
        },
        {
            field: 'description',
            headerName: 'Description',
            flex: 3,
            headerClassName: 'super-app-theme--header'
        },
        {
            field: 'lastRefreshedDateTime',
            headerName: 'Last Refreshed',
            flex: 1,
            headerClassName: 'super-app-theme--header'
        }
    ];

    const handleRowClick = (params) => {
        const alias = params.row.alias || params.row.name;
        navigate(`/apihub/${encodeURIComponent(alias)}`, {
            state: { productId: params.row.productId }
        });
    };

    return (
        <Grid container spacing={2} style={containerStyle}>
            <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h5">API Hub</Typography>
                    <Typography color="text.secondary">
                        Browse available data products. Click a product to see details and build your query.
                    </Typography>
                </Box>
            </Grid>
            {error && (
                <Grid item xs={12}>
                    <Alert severity="error">{typeof error === 'string' ? error : 'An error occurred loading data products.'}</Alert>
                </Grid>
            )}
            <Grid item xs={12}>
                {loading ? (
                    <Box>
                        <Skeleton variant="rectangular" height={50} sx={{ mb: 1 }} />
                        <Skeleton variant="rectangular" height={400} />
                    </Box>
                ) : (
                    <Box sx={{
                        height: 650,
                        width: '100%',
                        '& .super-app-theme--header': {
                            backgroundColor: '#A5D7DA',
                            fontWeight: 'bold'
                        }
                    }}>
                        <DataGrid
                            columns={columns}
                            rows={dataProducts}
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 300 }
                                }
                            }}
                            onRowClick={handleRowClick}
                            pageSizeOptions={[10, 25, 50]}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 10 } }
                            }}
                            sx={{
                                '& .MuiDataGrid-row:hover': {
                                    cursor: 'pointer'
                                }
                            }}
                        />
                    </Box>
                )}
            </Grid>
        </Grid>
    );
};

export default ApiHubPage;
